import {
  useState,
  useRef,
  useCallback,
  useId,
  useLayoutEffect,
  type ReactNode,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";
import styles from "./Tooltip.module.css";

export interface TooltipProps {
  title?: ReactNode;
  content: ReactNode;
  children: ReactNode;
  className?: string;
  tabIndex?: number | undefined;
}

const SHOW_DELAY_MS = 120;
const FLIP_THRESHOLD_PX = 170;
const EDGE_MARGIN = 8;
const HALF_WIDTH = 140;

type Placement = "top" | "bottom";

interface PopupPos {
  top: number;
  left: number;
  placement: Placement;
}

/**
 * Hover/focus tooltip rendered through a portal to document.body, so it escapes
 * any parent stacking context or `overflow: hidden` and is never clipped by
 * neighbouring cards or frames. Position is measured from the trigger on open
 * and kept in sync on scroll/resize.
 */
export function Tooltip({
  title,
  content,
  children,
  className,
  tabIndex = 0,
}: TooltipProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PopupPos | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  const measure = useCallback((): PopupPos | null => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) {
      return null;
    }
    const placement: Placement = rect.top < FLIP_THRESHOLD_PX ? "bottom" : "top";
    const vw = window.innerWidth;
    const centerX = rect.left + rect.width / 2;
    const left = Math.min(Math.max(centerX, HALF_WIDTH + EDGE_MARGIN), vw - HALF_WIDTH - EDGE_MARGIN);
    const top = placement === "bottom" ? rect.bottom + EDGE_MARGIN : rect.top - EDGE_MARGIN;
    return { top, left, placement };
  }, []);

  const show = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setPos(measure());
      setOpen(true);
    }, SHOW_DELAY_MS);
  }, [measure]);

  const hide = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(false);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === "Escape") {
        hide();
      }
    },
    [hide],
  );

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    const update = (): void => setPos(measure());
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, measure]);

  return (
    <span
      ref={wrapperRef}
      className={cx(styles.wrapper, className)}
      tabIndex={tabIndex}
      aria-describedby={tooltipId}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={handleKeyDown}
    >
      {children}
      {open &&
        pos &&
        typeof document !== "undefined" &&
        createPortal(
          <span
            id={tooltipId}
            role="tooltip"
            className={cx(styles.popup, pos.placement === "bottom" && styles.popupBottom)}
            style={{ top: pos.top, left: pos.left }}
          >
            {title != null && <span className={styles.title}>{title}</span>}
            <span className={styles.content}>{content}</span>
            <span className={styles.arrow}>
              <span className={styles.arrowInner} />
            </span>
          </span>,
          document.body,
        )}
    </span>
  );
}
